import { useAuth, useUser } from "@clerk/clerk-react";
import type { UserAccessProfile } from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import {
	ChevronRight,
	LogOut,
	type LucideIcon,
	UserCog,
	X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchAccessProfileQueryOptions } from "@/api/accessProfile";
import { LanguageSegmentedControl } from "@/components/LanguageSelector";
import { RoleBadge } from "@/components/RoleBadge";
import ThemeSelector from "@/components/ThemeSelector";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useCurrentOrganization } from "@/hooks/useCurrentOrganization";
import { useOrgNavigation } from "@/hooks/useOrgNavigation";
import { cn } from "@/utils/utils";
import { NotificationsToggle } from "../-components/NotificationsToggle";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/more/")({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		// Already warmed by the parent org layout loader — a cache hit, so the More
		// page adds no request path (defect gate §8 N+1: constant, no per-row fetch).
		await context.queryClient.ensureQueryData(
			fetchAccessProfileQueryOptions(userId, token, orgId),
		);

		return { userId, token, orgId };
	},
	component: MoreRouteComponent,
});

function MoreRouteComponent() {
	const { userId, token, orgId } = Route.useLoaderData();

	const { data: profile } = useSuspenseQuery(
		fetchAccessProfileQueryOptions(userId, token, orgId),
	);
	const currentOrg = useCurrentOrganization();

	return <MorePageContent profile={profile} userRole={currentOrg.role} />;
}

/** First up-to-two initials of a display name, for the avatar chip. */
function initialsFrom(name: string): string {
	return name
		.split(" ")
		.map((part) => part[0])
		.filter(Boolean)
		.slice(0, 2)
		.join("")
		.toUpperCase();
}

/** Nav ids already reachable as first-class bottom tabs — excluded from More. */
const TAB_IDS = new Set(["dashboard", "attendance", "customers"]);

/**
 * Mobile "More / Profile" page (2b, plan §7). Mirrors the desktop sidebar footer
 * hub: identity (Clerk), theme/language/notification preferences, the
 * permission-gated account & studio links (from the shared nav config, so it can
 * never drift from the sidebar's permission model), and a Clerk-delegated sign
 * out. Rendered as a testable presentational unit so the identity + gating can
 * be exercised without the route loader.
 */
export function MorePageContent({
	profile,
	userRole,
}: {
	profile: UserAccessProfile;
	/** Current-org role (e.g. "ORG_OWNER"); rendered as a pill in the identity card. */
	userRole?: string;
}) {
	const { t } = useTranslation();
	const { user } = useUser();
	const { signOut } = useAuth();
	const router = useRouter();

	// Account & studio links come straight from the shared nav config (already
	// permission-filtered), minus the three surfaced as bottom tabs — UI gating
	// preserved verbatim from the sidebar (defect gate §8 security).
	const navItems = useOrgNavigation(profile).filter(
		(item) => !TAB_IDS.has(item.id),
	);

	// Identity mirrors the desktop SidebarAccountMenu: full name on top, the email
	// beneath it — shown once, so a nameless account (which falls back to the email
	// on the first line) doesn't repeat it. Only the second-line email truncates.
	const email = user?.primaryEmailAddress?.emailAddress ?? "";
	const displayName = user?.fullName ?? email;
	const secondary = user?.fullName ? email : "";
	const initials = displayName ? initialsFrom(displayName) : "";

	// Delegated to Clerk — identical to the desktop SidebarAccountMenu; no
	// bespoke auth logic here (defect gate §8 security).
	const handleSignOut = () => {
		void signOut();
	};

	return (
		<div className="mx-auto max-w-md space-y-6 pb-4">
			<div className="flex items-center justify-between gap-3">
				<h1 className="text-2xl font-extrabold tracking-tight text-base-content">
					{t("more.title")}
				</h1>
				<button
					type="button"
					onClick={() => router.history.back()}
					aria-label={t("common.close")}
					className={cn(
						"flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-base-content/60 transition-colors",
						"hover:bg-base-200 hover:text-base-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
					)}
					data-testid="more-close"
				>
					<X className="h-5 w-5" aria-hidden="true" />
				</button>
			</div>

			{/* Account & studio group */}
			<section aria-label={t("more.accountStudio")}>
				<GroupLabel>{t("more.accountStudio")}</GroupLabel>
				<nav className="divide-y divide-base-300 overflow-hidden rounded-2xl border border-base-300 bg-base-100">
					<MoreRow
						to="/app/user-settings"
						icon={UserCog}
						label={t("userSettings.title")}
					/>
					{navItems.map((item) =>
						item.children ? (
							<div key={item.id}>
								<p className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-base-content/40">
									{t(item.labelKey)}
								</p>
								{item.children.map((child) => (
									<MoreRow
										key={child.id}
										to={child.to ?? ""}
										params={child.params}
										search={child.search}
										label={t(child.labelKey)}
										isActive={child.isActive}
									/>
								))}
							</div>
						) : (
							<MoreRow
								key={item.id}
								to={item.to ?? ""}
								params={item.params}
								search={item.search}
								icon={item.icon}
								label={t(item.labelKey)}
								isActive={item.isActive}
							/>
						),
					)}
				</nav>
			</section>

			{/* Preferences group */}
			<section aria-label={t("more.preferences")}>
				<GroupLabel>{t("more.preferences")}</GroupLabel>
				<div className="divide-y divide-base-300 overflow-hidden rounded-2xl border border-base-300 bg-base-100">
					<div className="flex items-center justify-between gap-3 px-4 py-3.5">
						<span className="text-sm font-medium text-base-content">
							{t("theme.title")}
						</span>
						<ThemeSelector />
					</div>
					<div className="flex items-center justify-between gap-3 px-4 py-3.5">
						<span className="text-sm font-medium text-base-content">
							{t("common.language")}
						</span>
						<LanguageSegmentedControl />
					</div>
					<NotificationsToggle />
				</div>
			</section>

			{/* Profile card → user settings */}
			<Link
				to="/app/user-settings"
				aria-label={t("more.openProfileSettings")}
				className={cn(
					"flex min-h-[44px] items-center gap-3 rounded-2xl border border-base-300 bg-base-100 px-4 py-3.5 transition-colors",
					"hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
				)}
				data-testid="more-profile-card"
			>
				<span
					className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-2xl bg-primary text-base font-bold text-primary-content"
					aria-hidden="true"
				>
					{initials}
				</span>
				<span className="min-w-0 flex-1">
					<span className="block text-base font-semibold text-base-content">
						{displayName}
					</span>
					{secondary && (
						<span className="block truncate text-sm text-base-content/60">
							{secondary}
						</span>
					)}
					<RoleBadge userRole={userRole} />
				</span>
				<ChevronRight
					className="h-5 w-5 shrink-0 text-base-content/30"
					aria-hidden="true"
				/>
			</Link>

			{/* Sign out — delegated to Clerk */}
			<button
				type="button"
				onClick={handleSignOut}
				className={cn(
					"flex min-h-[44px] w-full items-center justify-center gap-2 rounded-2xl border border-error/40 px-4 py-3 text-sm font-semibold text-error transition-colors",
					"hover:bg-error/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error/40",
				)}
				data-testid="more-sign-out"
			>
				<LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
				{t("auth.signOut")}
			</button>
		</div>
	);
}

function GroupLabel({ children }: { children: React.ReactNode }) {
	return (
		<p className="px-1 pb-2 text-xs font-semibold uppercase tracking-wide text-base-content/40">
			{children}
		</p>
	);
}

function MoreRow({
	to,
	params,
	search,
	icon: Icon,
	label,
	isActive,
}: {
	to: string;
	params?: Record<string, string>;
	search?: Record<string, unknown>;
	icon?: LucideIcon;
	label: string;
	isActive?: boolean;
}) {
	return (
		<Link
			to={to}
			params={params}
			search={search}
			aria-current={isActive ? "page" : undefined}
			className={cn(
				"flex min-h-[52px] items-center gap-3 px-4 py-3 text-sm font-medium text-base-content transition-colors",
				"hover:bg-base-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40",
				"aria-[current=page]:text-primary",
			)}
		>
			{Icon ? (
				<Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
			) : (
				<span className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
			)}
			<span className="flex-1 break-words">{label}</span>
			<ChevronRight
				className="h-4 w-4 shrink-0 text-base-content/30"
				aria-hidden="true"
			/>
		</Link>
	);
}
